import { useQuery } from '@tanstack/react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { usePostApiArbitrageAccept } from '@/api/arbitrage/arbitrage';
import { postApiUniversignCheckSignerIdentity } from '@/api/universign/universign';
import { ROUTE } from '@/constants/route';
import {
  isIdentityValidationFailed,
  toUniversignErrorState,
} from '@/utils/functions/identityValidationError/identityValidationError';

import type { ArbitrageSignatureState } from '@/pages/ArbitrageSignature/ArbitrageSignature';

const useCheckArbitrageSignerIdentity = (numContrat?: string, idPlacement?: string) => {
  const navigate = useNavigate();
  const acceptMutation = usePostApiArbitrageAccept();

  const {
    data: signerData,
    isFetching: isChecking,
    isError: checkError,
    refetch,
  } = useQuery({
    queryKey: ['universign-check-signer-identity', numContrat],
    queryFn: ({ signal }) => postApiUniversignCheckSignerIdentity({ numContrat: numContrat ?? '' }, signal),
    enabled: Boolean(numContrat),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });

  const proceed = async (id: string): Promise<boolean> => {
    if (!signerData) return false;
    try {
      if (signerData.identityKnown) {
        const response = await acceptMutation.mutateAsync({
          arbitrageId: id,
          data: { subscriptionId: signerData?.idSouscription ?? null },
        });

        // Universign refuse la pièce déjà connue : renvoyer le client re-déposer ses justificatifs,
        // avec le motif du refus pour que le formulaire affiche le message dédié.
        if (isIdentityValidationFailed(response)) {
          if (signerData.idSouscription == null) return false;
          navigate(generatePath(ROUTE.ARBITRAGEJUSTIFICATIFS, { id }), {
            state: {
              idSouscription: signerData.idSouscription,
              idPlacement,
              universignError: toUniversignErrorState(response),
            },
          });
          return true;
        }

        if (!response.url) return false;
        navigate(generatePath(ROUTE.ARBITRAGESIGNATURE, { id }), {
          state: { url: response.url } satisfies ArbitrageSignatureState,
        });
      } else {
        if (signerData.idSouscription == null) return false;
        navigate(generatePath(ROUTE.ARBITRAGEJUSTIFICATIFS, { id }), {
          state: { idSouscription: signerData.idSouscription, idPlacement },
        });
      }
      return true;
    } catch {
      return false;
    }
  };

  return { signerData, isChecking, checkError, refetch, proceed, isPending: acceptMutation.isPending };
};

export default useCheckArbitrageSignerIdentity;
